import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchReturns } from '@/services/returns';
import { fetchSupplierReturns } from '@/services/supplierReturns';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { ReturnRecord, SupplierReturn } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { CreateReturnForm } from '@/features/returns/CreateReturnForm';
import { ReturnDetail } from '@/features/returns/ReturnDetail';
import { CreateSupplierReturnModal } from '@/features/supplierReturns/CreateSupplierReturnModal';
import { SupplierReturnDetail } from '@/features/supplierReturns/SupplierReturnDetail';
import { useAuth } from '@/context/AuthContext';
import {
  RotateCcw,
  Plus,
  ChevronDown,
  CalendarDays,
  User,
  Loader2,
  AlertCircle,
  Building2,
} from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const ReturnsPage: React.FC = () => {
  const { role } = useAuth();
  const isManagerOrOwner = role === 'owner' || role === 'manager';

  const [activeTab, setActiveTab] = useState<'customer' | 'supplier'>('customer');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);

  // Customer Return Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailReturnId, setDetailReturnId] = useState<string | null>(null);

  // Supplier Return Modals
  const [createSupplierModalOpen, setCreateSupplierModalOpen] = useState(false);
  const [detailSupplierReturnId, setDetailSupplierReturnId] = useState<string | null>(null);

  // Customer returns query
  const {
    data: customerData,
    isLoading: loadingCustomerReturns,
    isError: isCustomerError,
    error: customerError,
    refetch: refetchCustomerReturns,
  } = useQuery({
    queryKey: queryKeys.returns.list({ search, status: statusFilter, page }),
    queryFn: () => fetchReturns({ search, status: statusFilter, page }),
    enabled: activeTab === 'customer',
    placeholderData: (prev) => prev,
  });

  // Supplier returns query
  const {
    data: supplierData,
    isLoading: loadingSupplierReturns,
    isError: isSupplierError,
    error: supplierError,
    refetch: refetchSupplierReturns,
  } = useQuery({
    queryKey: ['supplier-returns', 'list', { search, status: statusFilter, page }],
    queryFn: () => fetchSupplierReturns({ search, status: statusFilter, page }),
    enabled: activeTab === 'supplier' && isManagerOrOwner,
    placeholderData: (prev) => prev,
  });

  const customerReturns = customerData?.data ?? [];
  const customerTotalCount = customerData?.count ?? 0;

  const supplierReturns = supplierData?.data ?? [];
  const supplierTotalCount = supplierData?.count ?? 0;

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(0);
  };

  const handleCustomerCreateSuccess = (returnId: string) => {
    setCreateModalOpen(false);
    void refetchCustomerReturns();
    setDetailReturnId(returnId);
  };

  const handleSupplierCreateSuccess = (returnId: string) => {
    setCreateSupplierModalOpen(false);
    void refetchSupplierReturns();
    setDetailSupplierReturnId(returnId);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <RotateCcw className="w-5 h-5 text-amber-600" />
            <span>Returns & Refunds Management</span>
          </h1>
          <p className="text-xs text-brand-muted mt-0.5">
            Manage customer sales returns and purchase supplier returns
          </p>
        </div>

        {isManagerOrOwner && (
          <div className="flex items-center space-x-2">
            {activeTab === 'customer' ? (
              <button
                onClick={() => setCreateModalOpen(true)}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
                id="process-return-btn"
              >
                <Plus className="w-4 h-4" />
                <span>Process Customer Return</span>
              </button>
            ) : (
              <button
                onClick={() => setCreateSupplierModalOpen(true)}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-700 text-white text-sm font-bold rounded-md hover:bg-emerald-800 transition-colors shadow-sm"
                id="process-supplier-return-btn"
              >
                <Plus className="w-4 h-4" />
                <span>Process Supplier Return</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center space-x-2 border-b border-brand-border pb-1">
        <button
          type="button"
          onClick={() => { setActiveTab('customer'); setPage(0); setSearch(''); }}
          className={`flex items-center space-x-2 px-4 py-2 text-xs font-bold rounded-t-lg transition-colors border-b-2 ${
            activeTab === 'customer'
              ? 'border-brand-green text-brand-green bg-brand-bg'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          <User className="w-4 h-4" />
          <span>Customer Returns ({customerTotalCount})</span>
        </button>

        {isManagerOrOwner && (
          <button
            type="button"
            onClick={() => { setActiveTab('supplier'); setPage(0); setSearch(''); }}
            className={`flex items-center space-x-2 px-4 py-2 text-xs font-bold rounded-t-lg transition-colors border-b-2 ${
              activeTab === 'supplier'
                ? 'border-emerald-700 text-emerald-700 bg-brand-bg'
                : 'border-transparent text-brand-muted hover:text-brand-black'
            }`}
          >
            <Building2 className="w-4 h-4" />
            <span>Supplier Returns ({supplierTotalCount})</span>
          </button>
        )}
      </div>

      {/* Staff restriction alert */}
      {!isManagerOrOwner && activeTab === 'customer' && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center space-x-2 text-amber-800 text-xs">
          <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
          <span>Note: Processing returns requires owner or manager authorization. You can view return logs.</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder={activeTab === 'customer' ? 'Search return #, reason or notes…' : 'Search supplier return #, reason or notes…'}
          />
        </div>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>
      </div>

      {/* Customer Returns Tab Content */}
      {activeTab === 'customer' && (
        <>
          {isCustomerError && (
            <ErrorAlert message={parseDatabaseError(customerError)} onRetry={() => refetchCustomerReturns()} />
          )}

          {loadingCustomerReturns && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          )}

          {!loadingCustomerReturns && !isCustomerError && (
            <>
              {customerReturns.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <RotateCcw className="w-12 h-12 text-brand-muted/30 mb-3" />
                  <p className="text-sm font-semibold text-brand-black">No customer returns found</p>
                  <p className="text-xs text-brand-muted mt-1">
                    {search || statusFilter ? 'Try adjusting your search or status filter.' : 'Processed customer returns will appear here.'}
                  </p>
                </div>
              ) : (
                <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-4 py-3">Return Date</th>
                          <th className="text-left px-4 py-3">Return #</th>
                          <th className="text-left px-4 py-3">Invoice Ref</th>
                          <th className="text-left px-4 py-3">Customer</th>
                          <th className="text-left px-4 py-3">Reason</th>
                          <th className="text-right px-4 py-3">Refund Issued</th>
                          <th className="text-left px-4 py-3">Method</th>
                          <th className="text-left px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-brand-border">
                        {customerReturns.map((ret: ReturnRecord) => (
                          <tr
                            key={ret.id}
                            onClick={() => setDetailReturnId(ret.id)}
                            className="hover:bg-brand-bg/60 cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                              <span className="flex items-center space-x-1.5">
                                <CalendarDays className="w-3.5 h-3.5 shrink-0 text-amber-600" />
                                <span>{formatDate(ret.return_date)}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 font-bold text-brand-black">
                              #{ret.return_number}
                            </td>
                            <td className="px-4 py-3 font-medium text-brand-black">
                              #{ret.sale?.invoice_number || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-brand-muted">
                              <span className="flex items-center space-x-1.5 font-medium text-brand-black">
                                <User className="w-3.5 h-3.5 shrink-0 text-brand-muted" />
                                <span>{ret.customer?.name ?? 'Walk-in Customer'}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 text-brand-muted max-w-xs truncate">
                              {ret.reason || '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-brand-green">
                              {formatCurrency(ret.refund_amount)}
                            </td>
                            <td className="px-4 py-3 text-brand-black uppercase font-semibold text-[10px]">
                              {ret.refund_method || 'cash'}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${
                                  ret.status === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {ret.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {customerTotalCount > PAGE_SIZE && (
                <Pagination page={page} totalCount={customerTotalCount} onPageChange={setPage} />
              )}
            </>
          )}
        </>
      )}

      {/* Supplier Returns Tab Content */}
      {activeTab === 'supplier' && isManagerOrOwner && (
        <>
          {isSupplierError && (
            <ErrorAlert message={parseDatabaseError(supplierError)} onRetry={() => refetchSupplierReturns()} />
          )}

          {loadingSupplierReturns && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          )}

          {!loadingSupplierReturns && !isSupplierError && (
            <>
              {supplierReturns.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Building2 className="w-12 h-12 text-brand-muted/30 mb-3" />
                  <p className="text-sm font-semibold text-brand-black">No supplier returns found</p>
                  <p className="text-xs text-brand-muted mt-1">
                    {search || statusFilter ? 'Try adjusting your search or status filter.' : 'Supplier returns and RMAs will appear here.'}
                  </p>
                </div>
              ) : (
                <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-4 py-3">Return Date</th>
                          <th className="text-left px-4 py-3">Return #</th>
                          <th className="text-left px-4 py-3">Supplier</th>
                          <th className="text-left px-4 py-3">Purchase Invoice</th>
                          <th className="text-left px-4 py-3">Reason</th>
                          <th className="text-right px-4 py-3">Return Value</th>
                          <th className="text-right px-4 py-3">Payable Credit</th>
                          <th className="text-right px-4 py-3">Cash Refund</th>
                          <th className="text-left px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-brand-border">
                        {supplierReturns.map((sRet: SupplierReturn) => (
                          <tr
                            key={sRet.id}
                            onClick={() => setDetailSupplierReturnId(sRet.id)}
                            className="hover:bg-brand-bg/60 cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                              <span className="flex items-center space-x-1.5">
                                <CalendarDays className="w-3.5 h-3.5 shrink-0 text-emerald-700" />
                                <span>{formatDate(sRet.return_date)}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 font-bold text-brand-black">
                              #{sRet.return_number}
                            </td>
                            <td className="px-4 py-3 font-medium text-brand-black">
                              {sRet.supplier?.name || 'Supplier'}
                            </td>
                            <td className="px-4 py-3 text-brand-muted">
                              {sRet.purchase ? `#${sRet.purchase.purchase_number}` : 'Direct Return'}
                            </td>
                            <td className="px-4 py-3 text-brand-muted max-w-xs truncate">
                              {sRet.reason || '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-700">
                              {formatCurrency(sRet.return_value)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-brand-black">
                              {formatCurrency(sRet.credit_applied_to_payable)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-brand-green">
                              {formatCurrency(sRet.refund_amount)}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full uppercase bg-emerald-100 text-emerald-800">
                                {sRet.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {supplierTotalCount > PAGE_SIZE && (
                <Pagination page={page} totalCount={supplierTotalCount} onPageChange={setPage} />
              )}
            </>
          )}
        </>
      )}

      {/* Customer Return Modals */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Process Customer Return & Refund"
        maxWidth="lg"
      >
        <CreateReturnForm
          onSuccess={handleCustomerCreateSuccess}
          onCancel={() => setCreateModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={!!detailReturnId}
        onClose={() => setDetailReturnId(null)}
        title="Return Record Details"
        maxWidth="lg"
      >
        {detailReturnId && (
          <ReturnDetail
            returnId={detailReturnId}
            onClose={() => setDetailReturnId(null)}
          />
        )}
      </Modal>

      {/* Supplier Return Modals */}
      <CreateSupplierReturnModal
        isOpen={createSupplierModalOpen}
        onClose={() => setCreateSupplierModalOpen(false)}
        onSuccess={handleSupplierCreateSuccess}
      />

      <Modal
        isOpen={!!detailSupplierReturnId}
        onClose={() => setDetailSupplierReturnId(null)}
        title="Supplier Return Details"
        maxWidth="lg"
      >
        {detailSupplierReturnId && (
          <SupplierReturnDetail
            returnId={detailSupplierReturnId}
            onClose={() => setDetailSupplierReturnId(null)}
          />
        )}
      </Modal>
    </div>
  );
};

